import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import { Heart, X, Star, Sparkles, Filter, Loader2, MapPin, RefreshCw, Zap, Rocket, Clock, Info, Navigation, AlertCircle, RotateCcw, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';
import { NotificationBell } from '../components/NotificationCenter';
import { useGeolocation, formatDistance } from '../hooks/useGeolocation';
import MatchSuggestions from '../components/MatchSuggestions';
import { UserBadges } from '../components/UserBadges';
import BoostStatusModal from '../components/BoostStatusModal';

const INTERESTS = [
  'Rejser', 'Musik', 'Film', 'Læsning', 'Sport', 'Fitness', 'Madlavning', 'Kunst',
  'Fotografering', 'Gaming', 'Natur', 'Kæledyr', 'Kaffe', 'Vin', 'Dans', 'Yoga',
  'Vandreture', 'Strand', 'Teknologi', 'Mode', 'Mad', 'Koncerter', 'Museer', 'Natteliv'
];

const DISTANCE_OPTIONS = [
  { value: 5, label: '5 km' },
  { value: 10, label: '10 km' },
  { value: 25, label: '25 km' },
  { value: 50, label: '50 km' },
  { value: 100, label: '100 km' },
  { value: 250, label: '250 km' },
  { value: null, label: 'Ingen grænse' }
];

// Helper to load saved filters from localStorage
const loadSavedFilters = () => {
  try {
    const saved = localStorage.getItem('discovery_filters');
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error('Failed to load saved filters:', e);
  }
  return null;
};

const Discovery = () => {
  const [profiles, setProfiles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [swiping, setSwiping] = useState(false);
  const [showMatch, setShowMatch] = useState(null);
  const [showBoostModal, setShowBoostModal] = useState(false);
  const [showBoostStatusModal, setShowBoostStatusModal] = useState(false);
  const [showSuperlikeModal, setShowSuperlikeModal] = useState(false);
  const [boostStatus, setBoostStatus] = useState({ is_boosted: false, minutes_remaining: 0 });
  const [boostPackages, setBoostPackages] = useState({});
  const [boostLoading, setBoostLoading] = useState(null);
  const [superlikeStatus, setSuperlikeStatus] = useState({ can_superlike: false, remaining: 0 });
  const [likesCount, setLikesCount] = useState(0);
  const [undoLoading, setUndoLoading] = useState(false);
  
  // Load filters from localStorage on initial mount
  const savedFilters = loadSavedFilters();
  const [filters, setFilters] = useState(savedFilters?.filters || {
    min_age: 18,
    max_age: 50,
    gender: '',
    location: '',
    interests: [],
    max_distance_km: null
  });
  const [useDistanceFilter, setUseDistanceFilter] = useState(savedFilters?.useDistanceFilter || false);
  const [filtersApplied, setFiltersApplied] = useState(savedFilters?.filtersApplied || false);

  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  // Geolocation hook
  const { 
    location, 
    loading: locationLoading, 
    error: locationError, 
    permissionStatus,
    requestLocation,
    loadSavedLocation 
  } = useGeolocation(token);

  const handleViewProfile = (profileId) => {
    navigate(`/profile/${profileId}`);
  };

  // Fetch who liked me count
  const fetchLikesCount = async () => {
    try {
      const res = await axios.get(`${API}/likes/received`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLikesCount(res.data.total_count);
    } catch (err) {
      // Silently fail
    }
  };

  // Undo last swipe
  const handleUndo = async () => {
    if (!user?.is_premium || !['gold', 'platinum'].includes(user?.premium_tier)) {
      toast.error('Fortryd swipe er en premium funktion (Guld/Platin)');
      return;
    }
    
    setUndoLoading(true);
    try {
      const res = await axios.post(`${API}/swipe/undo`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.undone_user) {
        // Add profile back to the front
        setProfiles([res.data.undone_user, ...profiles]);
        setCurrentIndex(0);
        toast.success(`Swipe fortrudt! ${res.data.remaining_undos >= 0 ? `(${res.data.remaining_undos} tilbage i dag)` : ''}`);
      } else {
        toast.success(res.data.message || 'Swipe fortrydt!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke fortryde swipe');
    } finally {
      setUndoLoading(false);
    }
  };

  const fetchProfiles = async (maxDistance = null) => {
    setLoading(true);
    try {
      let url = `${API}/discover?limit=20`;
      if (maxDistance !== null) {
        url += `&max_distance_km=${maxDistance}`;
      }
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfiles(res.data);
      setCurrentIndex(0);
    } catch (err) {
      toast.error('Kunne ikke indlæse profiler');
    } finally {
      setLoading(false);
    }
  };

  const toggleFilterInterest = (interest) => {
    if (filters.interests.includes(interest)) {
      setFilters({ ...filters, interests: filters.interests.filter(i => i !== interest) });
    } else {
      setFilters({ ...filters, interests: [...filters.interests, interest] });
    }
  };

  const searchWithFilters = async () => {
    setLoading(true);
    try {
      const searchFilters = {
        ...filters,
        max_distance_km: useDistanceFilter && location ? filters.max_distance_km : null
      };
      const res = await axios.post(`${API}/discover/search`, searchFilters, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfiles(res.data);
      setCurrentIndex(0);
      setFiltersApplied(true);
      
      // Save filters to localStorage
      localStorage.setItem('discovery_filters', JSON.stringify({
        filters,
        useDistanceFilter,
        filtersApplied: true
      }));
      
      toast.success(`Fandt ${res.data.length} profiler med dine filtre`);
    } catch (err) {
      toast.error('Kunne ikke søge i profiler');
    } finally {
      setLoading(false);
    }
  };

  const fetchBoostStatus = async () => {
    try {
      const res = await axios.get(`${API}/boost/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBoostStatus(res.data);
    } catch (err) {
      console.error('Kunne ikke hente boost status');
    }
  };

  const fetchBoostPackages = async () => {
    try {
      const res = await axios.get(`${API}/boost/packages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBoostPackages(res.data);
    } catch (err) {
      console.error('Kunne ikke hente boost pakker');
    }
  };

  const fetchSuperlikeStatus = async () => {
    try {
      const res = await axios.get(`${API}/superlikes/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuperlikeStatus(res.data);
    } catch (err) {
      console.error('Kunne ikke hente superlike status');
    }
  };

  const handleBoostPurchase = async (packageId) => {
    setBoostLoading(packageId);
    try {
      const originUrl = window.location.origin;
      const res = await axios.post(`${API}/boost/checkout`, {
        boost_package_id: packageId,
        origin_url: originUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke starte checkout');
      setBoostLoading(null);
    }
  };

  useEffect(() => {
    // Load saved location first
    loadSavedLocation();
    
    // If filters were applied, use them, otherwise fetch all profiles
    if (filtersApplied) {
      searchWithFilters();
    } else {
      fetchProfiles();
    }
    
    fetchBoostStatus();
    fetchBoostPackages();
    fetchSuperlikeStatus();
    fetchLikesCount();
    
    // Update boost status every minute
    const interval = setInterval(() => {
      fetchBoostStatus();
      fetchSuperlikeStatus();
      fetchLikesCount();
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Request location automatically if permission is already granted
  useEffect(() => {
    if (permissionStatus === 'granted' && !location) {
      requestLocation();
    }
  }, [permissionStatus]);

  const handleSwipe = async (action) => {
    if (swiping || currentIndex >= profiles.length) return;
    
    const targetUser = profiles[currentIndex];
    setSwiping(true);

    try {
      const res = await axios.post(`${API}/swipe`, {
        target_user_id: targetUser.id,
        action: action
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.is_match) {
        setShowMatch({ 
          ...targetUser, 
          wasSuperlike: action === 'superlike',
          matched_via_boost: res.data.match?.is_boost_match || false
        });
      } else if (action === 'superlike') {
        toast.success('Super Like sendt! 💫');
      }

      // Update superlike status after using one
      if (action === 'superlike') {
        fetchSuperlikeStatus();
      }

      setCurrentIndex(prev => prev + 1);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error(err.response.data.detail);
      } else {
        toast.error('Kunne ikke registrere handling');
      }
    } finally {
      setSwiping(false);
    }
  };

  const currentProfile = profiles[currentIndex];

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Heart className="w-6 h-6 text-primary fill-primary" />
            <span className="text-lg font-display font-semibold text-foreground">Opdag</span>
          </div>
          <div className="flex items-center gap-2">
            {/* Who Likes Me */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/likes')}
              className="rounded-full relative"
              data-testid="who-likes-me-btn"
            >
              <Eye className="w-5 h-5 text-primary" />
              {likesCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center bg-primary text-white text-[10px] font-bold rounded-full">
                  {likesCount > 99 ? '99+' : likesCount}
                </span>
              )}
            </Button>
            
            {/* Notification Bell */}
            <NotificationBell />
            
            {/* Boost Button */}
            {boostStatus.is_boosted ? (
              <button
                onClick={() => setShowBoostStatusModal(true)}
                className="flex items-center gap-1.5 bg-gradient-to-r from-accent to-orange-500 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg shadow-accent/30 animate-pulse hover:shadow-accent/50 transition-shadow"
                data-testid="boost-status-btn"
              >
                <Zap className="w-4 h-4" />
                <span>
                  {boostStatus.minutes_remaining >= 60 
                    ? `${Math.floor(boostStatus.minutes_remaining / 60)}t ${boostStatus.minutes_remaining % 60}m`
                    : `${boostStatus.minutes_remaining}m`
                  }
                </span>
              </button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowBoostModal(true)}
                className="rounded-full border-accent text-accent hover:bg-accent/10"
                data-testid="boost-btn"
              >
                <Rocket className="w-4 h-4 mr-1" />
                Boost
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={() => {
              setFiltersApplied(false);
              localStorage.removeItem('discovery_filters');
              fetchProfiles();
            }} className="rounded-full" data-testid="refresh-btn">
              <RefreshCw className="w-5 h-5" />
            </Button>
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" size="icon" className={`rounded-full relative ${filtersApplied ? 'border-primary text-primary' : ''}`} data-testid="filter-btn">
                  <Filter className="w-5 h-5" />
                  {filtersApplied && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-primary rounded-full" />
                  )}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-sm rounded-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-display">Filtrer Profiler</DialogTitle>
                </DialogHeader>
                <div className="space-y-6 py-4">
                  {/* Distance Filter */}
                  <div className="space-y-3 p-3 bg-muted/50 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Navigation className="w-4 h-4 text-primary" />
                        <Label>Afstandsfilter</Label>
                      </div>
                      <Switch
                        checked={useDistanceFilter}
                        onCheckedChange={setUseDistanceFilter}
                        disabled={!location}
                      />
                    </div>
                    
                    {!location ? (
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground">
                          Aktiver lokation for at filtrere efter afstand
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={requestLocation}
                          disabled={locationLoading}
                          className="w-full rounded-full"
                        >
                          {locationLoading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <MapPin className="w-4 h-4 mr-2" />
                          )}
                          Aktiver lokation
                        </Button>
                        {locationError && (
                          <p className="text-xs text-destructive flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {locationError}
                          </p>
                        )}
                      </div>
                    ) : useDistanceFilter && (
                      <div className="space-y-2">
                        <Label className="text-sm">
                          Maks afstand: {filters.max_distance_km ? `${filters.max_distance_km} km` : 'Ingen grænse'}
                        </Label>
                        <Select 
                          value={filters.max_distance_km?.toString() || 'none'} 
                          onValueChange={(v) => setFilters({ 
                            ...filters, 
                            max_distance_km: v === 'none' ? null : parseInt(v) 
                          })}
                        >
                          <SelectTrigger className="rounded-xl">
                            <SelectValue placeholder="Vælg afstand" />
                          </SelectTrigger>
                          <SelectContent>
                            {DISTANCE_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value || 'none'} value={opt.value?.toString() || 'none'}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <Label>Aldersinterval: {filters.min_age} - {filters.max_age}</Label>
                    <Slider
                      value={[filters.min_age, filters.max_age]}
                      onValueChange={([min, max]) => setFilters({ ...filters, min_age: min, max_age: max })}
                      min={18}
                      max={80}
                      step={1}
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Køn</Label>
                    <Select value={filters.gender} onValueChange={(v) => setFilters({ ...filters, gender: v })}>
                      <SelectTrigger className="rounded-xl">
                        <SelectValue placeholder="Alle" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Alle</SelectItem>
                        <SelectItem value="male">Mand</SelectItem>
                        <SelectItem value="female">Kvinde</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Lokation (tekst)</Label>
                    <Input
                      placeholder="By eller land"
                      value={filters.location}
                      onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                      className="rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Interesser {filters.interests.length > 0 && `(${filters.interests.length} valgt)`}</Label>
                    <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                      {INTERESTS.map((interest) => (
                        <Badge
                          key={interest}
                          variant={filters.interests.includes(interest) ? 'default' : 'outline'}
                          className={`cursor-pointer px-2 py-1 text-xs ${
                            filters.interests.includes(interest)
                              ? 'bg-primary text-primary-foreground'
                              : 'hover:bg-muted'
                          }`}
                          onClick={() => toggleFilterInterest(interest)}
                        >
                          {interest}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <DialogClose asChild>
                    <Button onClick={searchWithFilters} className="w-full rounded-full bg-primary hover:bg-primary/90">
                      Anvend Filtre
                    </Button>
                  </DialogClose>
                  {(filters.gender || filters.location || filters.interests.length > 0 || useDistanceFilter || filtersApplied) && (
                    <DialogClose asChild>
                      <Button 
                        variant="ghost" 
                        onClick={() => {
                          setFilters({ min_age: 18, max_age: 50, gender: '', location: '', interests: [], max_distance_km: null });
                          setUseDistanceFilter(false);
                          setFiltersApplied(false);
                          localStorage.removeItem('discovery_filters');
                          fetchProfiles();
                        }} 
                        className="w-full rounded-full text-muted-foreground"
                      >
                        Nulstil Filtre
                      </Button>
                    </DialogClose>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="pt-20 px-4">
        <div className="max-w-lg mx-auto">
          {/* AI Match Suggestions */}
          {!loading && (
            <MatchSuggestions 
              onLike={async (userId) => {
                try {
                  const response = await axios.post(`${API}/swipe`, {
                    target_user_id: userId,
                    action: 'like'
                  }, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  
                  if (response.data.is_match) {
                    setShowMatch(userId);
                    toast.success('🎉 Det er et match!');
                  } else {
                    toast.success('Like sendt!');
                  }
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Kunne ikke like');
                }
              }}
            />
          )}

          {loading ? (
            <div className="flex items-center justify-center h-[70vh]">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : profiles.length === 0 || currentIndex >= profiles.length ? (
            <div className="flex flex-col items-center justify-center h-[70vh] text-center">
              <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-4">
                <Heart className="w-10 h-10 text-muted-foreground" />
              </div>
              <h2 className="text-xl font-display font-semibold text-foreground">Ingen flere profiler</h2>
              <p className="text-muted-foreground mt-2 max-w-xs">
                Kom tilbage senere eller juster dine filtre for at se flere
              </p>
              <Button onClick={fetchProfiles} className="mt-6 rounded-full" data-testid="reload-profiles-btn">
                <RefreshCw className="w-4 h-4 mr-2" />
                Opdater
              </Button>
            </div>
          ) : (
            <div className="relative h-[65vh]">
              <AnimatePresence>
                <ProfileCard
                  key={currentProfile.id}
                  profile={currentProfile}
                  onSwipe={handleSwipe}
                  onViewProfile={handleViewProfile}
                />
              </AnimatePresence>
            </div>
          )}

          {/* Action Buttons */}
          {currentProfile && (
            <div className="flex items-center justify-center gap-4 mt-6">
              <Button
                size="lg"
                variant="outline"
                className="w-16 h-16 rounded-full border-2 border-destructive hover:bg-destructive/10"
                onClick={() => handleSwipe('pass')}
                disabled={swiping}
                data-testid="pass-btn"
              >
                <X className="w-8 h-8 text-destructive" />
              </Button>
              
              <Button
                size="lg"
                variant="outline"
                className={`w-14 h-14 rounded-full border-2 ${
                  superlikeStatus.can_superlike 
                    ? 'border-accent hover:bg-accent/10' 
                    : 'border-accent/50 hover:bg-accent/10'
                }`}
                onClick={() => {
                  if (superlikeStatus.can_superlike) {
                    handleSwipe('superlike');
                  } else {
                    setShowSuperlikeModal(true);
                  }
                }}
                disabled={swiping}
                title={superlikeStatus.can_superlike ? 'Send Super Like' : 'Få flere Super Likes'}
                data-testid="superlike-btn"
              >
                <Star className={`w-6 h-6 ${superlikeStatus.can_superlike ? 'text-accent' : 'text-accent/50'}`} />
              </Button>
              {superlikeStatus.can_superlike && superlikeStatus.remaining > 0 && superlikeStatus.remaining !== -1 && (
                <span className="absolute -top-1 left-1/2 transform -translate-x-1/2 bg-accent text-white text-xs px-2 py-0.5 rounded-full">
                  {superlikeStatus.remaining}
                </span>
              )}
              
              <Button
                size="lg"
                className="w-16 h-16 rounded-full bg-primary hover:bg-primary/90"
                onClick={() => handleSwipe('like')}
                disabled={swiping}
                data-testid="like-btn"
              >
                <Heart className="w-8 h-8 text-white" />
              </Button>
              
              {/* Undo button (Premium only) */}
              {user?.is_premium && ['gold', 'platinum'].includes(user?.premium_tier) && (
                <Button
                  size="lg"
                  variant="outline"
                  className="w-12 h-12 rounded-full border-2 border-yellow-500 hover:bg-yellow-500/10"
                  onClick={handleUndo}
                  disabled={undoLoading || swiping}
                  title="Fortryd sidste swipe"
                  data-testid="undo-btn"
                >
                  {undoLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin text-yellow-500" />
                  ) : (
                    <RotateCcw className="w-5 h-5 text-yellow-500" />
                  )}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Match Modal */}
      <AnimatePresence>
        {showMatch && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            onClick={() => setShowMatch(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-card rounded-3xl p-8 max-w-sm w-full text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative mx-auto w-24 h-24 mb-4">
                <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
                <div className="relative w-24 h-24 rounded-full bg-primary flex items-center justify-center">
                  <Heart className="w-12 h-12 text-white animate-heart" />
                </div>
              </div>
              
              <h2 className="text-3xl font-display font-bold text-foreground mb-2">Det er et Match!</h2>
              
              {/* Boost Badge */}
              {showMatch.matched_via_boost && (
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.3, type: "spring" }}
                  className="inline-flex items-center gap-1.5 bg-gradient-to-r from-accent to-orange-500 text-white px-4 py-1.5 rounded-full text-sm font-semibold mb-3"
                >
                  <Zap className="w-4 h-4" />
                  Matchet via Boost!
                </motion.div>
              )}
              
              <p className="text-muted-foreground mb-6">
                Du og {showMatch.name} kan lide hinanden
              </p>
              
              <div className="flex justify-center gap-4 mb-6">
                <img
                  src={user?.photos?.[0] || 'https://via.placeholder.com/100'}
                  alt="Dig"
                  className="w-20 h-20 rounded-full object-cover border-4 border-primary"
                />
                <img
                  src={showMatch.photos?.[0] || 'https://via.placeholder.com/100'}
                  alt={showMatch.name}
                  className="w-20 h-20 rounded-full object-cover border-4 border-primary"
                />
              </div>
              
              <div className="space-y-3">
                <Button
                  onClick={() => {
                    setShowMatch(null);
                    window.location.href = `/chat/${showMatch.id}`;
                  }}
                  className="w-full rounded-full bg-primary hover:bg-primary/90"
                  data-testid="send-message-btn"
                >
                  Send en besked
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setShowMatch(null)}
                  className="w-full rounded-full"
                >
                  Fortsæt med at swipe
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Boost Modal */}
      <AnimatePresence>
        {showBoostModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            onClick={() => setShowBoostModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-card rounded-3xl p-6 max-w-sm w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-accent to-accent/70 flex items-center justify-center mx-auto mb-4">
                  <Rocket className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-display font-bold text-foreground">Boost Din Profil</h2>
                <p className="text-muted-foreground mt-2">
                  Bliv set af flere og få op til 10x flere matches!
                </p>
              </div>

              <div className="space-y-3 mb-6">
                {Object.entries(boostPackages).map(([id, pkg]) => (
                  <button
                    key={id}
                    onClick={() => handleBoostPurchase(id)}
                    disabled={boostLoading !== null}
                    className="w-full flex items-center justify-between p-4 bg-muted/50 hover:bg-muted rounded-xl transition-colors"
                    data-testid={`boost-package-${id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                        <Clock className="w-5 h-5 text-accent" />
                      </div>
                      <div className="text-left">
                        <div className="font-semibold text-foreground">{pkg.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {pkg.duration_minutes >= 60 
                            ? `${Math.floor(pkg.duration_minutes / 60)} timer` 
                            : `${pkg.duration_minutes} minutter`}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {boostLoading === id ? (
                        <Loader2 className="w-5 h-5 animate-spin text-accent" />
                      ) : (
                        <span className="font-bold text-accent">{pkg.price} DKK</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>

              <Button
                variant="ghost"
                onClick={() => setShowBoostModal(false)}
                className="w-full rounded-full"
              >
                Måske senere
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Super Like Modal */}
      <AnimatePresence>
        {showSuperlikeModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            onClick={() => setShowSuperlikeModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-card rounded-3xl p-6 max-w-sm w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-accent to-yellow-500 flex items-center justify-center mx-auto mb-4">
                  <Star className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-display font-bold text-foreground">Super Likes</h2>
                <p className="text-muted-foreground mt-2">
                  Skil dig ud fra mængden og vis at du virkelig er interesseret!
                </p>
              </div>

              <div className="space-y-3 mb-6">
                {/* Premium option */}
                {!user?.is_premium && (
                  <button
                    onClick={() => {
                      setShowSuperlikeModal(false);
                      navigate('/premium');
                    }}
                    className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-primary/10 to-accent/10 hover:from-primary/20 hover:to-accent/20 rounded-xl transition-colors border border-primary/20"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-primary" />
                      </div>
                      <div className="text-left">
                        <div className="font-semibold text-foreground">Opgrader til Premium</div>
                        <div className="text-sm text-muted-foreground">Op til ubegrænsede Super Likes</div>
                      </div>
                    </div>
                    <div className="text-primary font-bold">→</div>
                  </button>
                )}

                {/* Super Like info for premium users */}
                {user?.is_premium && (
                  <div className="p-4 bg-muted/50 rounded-xl">
                    <div className="flex items-center gap-3 mb-2">
                      <Star className="w-5 h-5 text-accent" />
                      <span className="font-semibold text-foreground">Din Super Like Status</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {user.premium_tier === 'platinum' 
                        ? 'Du har ubegrænsede Super Likes med Platinum!' 
                        : user.premium_tier === 'gold'
                          ? 'Du har 5 Super Likes om dagen med Gold.'
                          : 'Du har 1 Super Like om dagen med Basic.'}
                    </p>
                    {superlikeStatus.remaining !== -1 && (
                      <p className="text-sm text-accent mt-1">
                        Tilbage i dag: {superlikeStatus.remaining}
                      </p>
                    )}
                  </div>
                )}

                {/* Trial option */}
                {!user?.is_premium && !user?.trial_used && (
                  <button
                    onClick={() => {
                      setShowSuperlikeModal(false);
                      navigate('/premium');
                    }}
                    className="w-full flex items-center justify-between p-4 bg-accent/10 hover:bg-accent/20 rounded-xl transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                        <Clock className="w-5 h-5 text-accent" />
                      </div>
                      <div className="text-left">
                        <div className="font-semibold text-foreground">Prøv Gratis i 3 Dage</div>
                        <div className="text-sm text-muted-foreground">Inkluderer Super Likes</div>
                      </div>
                    </div>
                    <div className="text-accent font-bold">Gratis</div>
                  </button>
                )}
              </div>

              <div className="text-center text-xs text-muted-foreground mb-4">
                <p>Super Likes giver 3x større chance for et match!</p>
              </div>

              <Button
                variant="ghost"
                onClick={() => setShowSuperlikeModal(false)}
                className="w-full rounded-full"
              >
                Måske senere
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Boost Status Modal */}
      <BoostStatusModal
        isOpen={showBoostStatusModal}
        onClose={() => setShowBoostStatusModal(false)}
        boostStatus={boostStatus}
        onBuyBoost={() => setShowBoostModal(true)}
      />

      <BottomNav />
    </div>
  );
};

// Profile Card Component
const ProfileCard = ({ profile, onSwipe, onViewProfile }) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-15, 15]);
  const opacity = useTransform(x, [-200, -100, 0, 100, 200], [0.5, 1, 1, 1, 0.5]);

  const likeOpacity = useTransform(x, [0, 100], [0, 1]);
  const nopeOpacity = useTransform(x, [-100, 0], [1, 0]);

  const handleDragEnd = (_, info) => {
    if (info.offset.x > 100) {
      onSwipe('like');
    } else if (info.offset.x < -100) {
      onSwipe('pass');
    }
  };

  const nextPhoto = (e) => {
    e.stopPropagation();
    if (profile.photos?.length > 1) {
      setCurrentPhotoIndex((prev) => (prev + 1) % profile.photos.length);
    }
  };

  const prevPhoto = (e) => {
    e.stopPropagation();
    if (profile.photos?.length > 1) {
      setCurrentPhotoIndex((prev) => (prev - 1 + profile.photos.length) % profile.photos.length);
    }
  };

  // Construct full image URL - handles both uploaded images (/uploads/...) and external URLs
  const getImageUrl = (photoPath) => {
    if (!photoPath) return 'https://images.pexels.com/photos/33534041/pexels-photo-33534041.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';
    if (photoPath.startsWith('http')) return photoPath;
    // For relative paths like /uploads/..., prepend backend URL
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${backendUrl}${photoPath}`;
  };

  const currentPhoto = getImageUrl(profile.photos?.[currentPhotoIndex]);

  return (
    <motion.div
      className="absolute inset-0 swipe-card"
      style={{ x, rotate, opacity }}
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      onDragEnd={handleDragEnd}
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.95, opacity: 0 }}
    >
      <div className="relative h-full rounded-3xl overflow-hidden shadow-xl bg-card">
        {/* Photo indicators */}
        {profile.photos?.length > 1 && (
          <div className="absolute top-4 left-4 right-4 flex gap-1 z-30">
            {profile.photos.map((_, i) => (
              <div
                key={i}
                className={`flex-1 h-1.5 rounded-full transition-colors ${
                  i === currentPhotoIndex ? 'bg-white' : 'bg-white/40'
                }`}
              />
            ))}
          </div>
        )}

        {/* Photo navigation - ONLY left/right sides for changing photos */}
        {profile.photos?.length > 1 && (
          <>
            <button
              onClick={prevPhoto}
              className="absolute left-0 top-0 w-1/3 h-2/3 z-20 cursor-pointer"
              aria-label="Forrige billede"
              data-testid="prev-photo-btn"
            />
            <button
              onClick={nextPhoto}
              className="absolute right-0 top-0 w-1/3 h-2/3 z-20 cursor-pointer"
              aria-label="Næste billede"
              data-testid="next-photo-btn"
            />
          </>
        )}
        
        <img
          src={currentPhoto}
          alt={profile.name}
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="profile-card-image"
        />
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        
        {/* Like/Nope indicators */}
        <motion.div
          className="absolute top-8 left-8 px-4 py-2 border-4 border-green-500 rounded-lg"
          style={{ opacity: likeOpacity }}
        >
          <span className="text-2xl font-bold text-green-500">LIKE</span>
        </motion.div>
        <motion.div
          className="absolute top-8 right-8 px-4 py-2 border-4 border-red-500 rounded-lg"
          style={{ opacity: nopeOpacity }}
        >
          <span className="text-2xl font-bold text-red-500">NEJ</span>
        </motion.div>
        
        {/* Profile info - clickable to view full profile */}
        <div 
          className="absolute bottom-0 left-0 right-0 p-6 text-white cursor-pointer"
          onClick={() => onViewProfile(profile.id)}
          data-testid="view-profile-btn"
        >
          <div className="flex items-end justify-between mb-3">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h2 className="text-3xl font-display font-bold">
                  {profile.name}{profile.age ? `, ${profile.age}` : ''}
                </h2>
                {/* Badges */}
                {profile.badges && profile.badges.length > 0 && (
                  <div className="flex items-center gap-1 bg-black/30 backdrop-blur-sm rounded-full px-2 py-1">
                    <UserBadges badges={profile.badges} size="sm" maxVisible={3} />
                  </div>
                )}
                {/* Info icon to indicate clickable */}
                <div className="bg-white/20 backdrop-blur-sm rounded-full p-1.5">
                  <Info className="w-4 h-4" />
                </div>
              </div>
              <div className="flex items-center gap-3 text-white/80 mt-1">
                {profile.location && (
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    <span>{profile.location}</span>
                  </div>
                )}
                {profile.distance_km !== null && profile.distance_km !== undefined && (
                  <div className="flex items-center gap-1">
                    <Navigation className="w-4 h-4" />
                    <span>{formatDistance(profile.distance_km)}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-2 items-end">
              {profile.is_boosted && (
                <div className="flex items-center gap-1 bg-accent/80 px-3 py-1 rounded-full">
                  <Zap className="w-4 h-4" />
                  <span className="text-sm font-medium">Boosted</span>
                </div>
              )}
              {profile.is_premium && (
                <div className="flex items-center gap-1 bg-primary/80 px-3 py-1 rounded-full">
                  <Sparkles className="w-4 h-4" />
                  <span className="text-sm font-medium">Premium</span>
                </div>
              )}
            </div>
          </div>
          
          {profile.bio && (
            <p className="text-white/90 text-sm line-clamp-2 mb-3">{profile.bio}</p>
          )}
          
          {profile.interests?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {profile.interests.slice(0, 4).map((interest, i) => (
                <Badge key={i} variant="secondary" className="bg-white/20 text-white border-none rounded-full">
                  {interest}
                </Badge>
              ))}
              {profile.interests.length > 4 && (
                <Badge variant="secondary" className="bg-white/20 text-white border-none rounded-full">
                  +{profile.interests.length - 4}
                </Badge>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default Discovery;
